import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import type { VerticalBreakdown } from '../../types';
import styles from './PipelineByVertical.module.css';

const VERTICAL_COLORS: Record<string, string> = {
  'Energy Transition & Services': '#E65100',
  'Resource Efficiency & Management': '#1565C0',
  'Climate Resilience & Adaptation': '#2E7D32',
};

const FALLBACK_COLOR = '#999';

interface Props {
  data: VerticalBreakdown[];
  /**
   * DISTINCT deals with at least one mapped vertical (A3). The slices count deal×vertical PAIRS — a
   * deal whose themes span two verticals sits in both — so their sum is not a deal count and must
   * not be the centre number. Absent (a cached pre-change payload) falls back to the slice sum,
   * which is exactly what the centre showed before this fix.
   */
  dealTotal?: number;
  /** Counted deals whose themes map to no vertical — footnoted so the donut stops hiding them (A2). */
  unclassified?: number;
}

export function PipelineByVertical({ data, dealTotal, unclassified = 0 }: Props) {
  if (data.length === 0) {
    return (
      <div className={styles.card}>
        <div className={styles.headerBar}>
          <span className={styles.headerLabel}>Pipeline by Vertical</span>
        </div>
        <div className={styles.empty}>No vertical data available</div>
        {/* "No vertical data" and "N deals have no vertical" are different statements, and the
            second is the one that explains the first — it must survive the empty branch. */}
        {unclassified > 0 && (
          <div className={styles.footnote}>
            {unclassified} deal{unclassified === 1 ? '' : 's'} without a vertical
          </div>
        )}
      </div>
    );
  }

  const chartData = data.map((d) => ({
    name: d.vertical,
    value: d.count,
  }));

  const sliceSum = chartData.reduce((sum, d) => sum + d.value, 0);
  const centreCount = dealTotal ?? sliceSum;

  return (
    <div className={styles.card}>
      <div className={styles.headerBar}>
        <span className={styles.headerLabel}>Pipeline by Vertical</span>
      </div>
      <div className={styles.body}>
        <div className={styles.chartWrap}>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={85}
                outerRadius={130}
                dataKey="value"
                paddingAngle={2}
                stroke="#fff"
                strokeWidth={2}
                label={false}
              >
                {chartData.map((entry) => (
                  <Cell
                    key={entry.name}
                    fill={VERTICAL_COLORS[entry.name] || FALLBACK_COLOR}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 12,
                  borderRadius: 6,
                  border: '1px solid #E5E5E0',
                }}
                formatter={(value, name) => [`${value} deals`, name]}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className={styles.centerLabel}>
            <span className={styles.centerValue}>{centreCount}</span>
            <span className={styles.centerSub}>deals</span>
          </div>
        </div>
        <div className={styles.legend}>
          {chartData.map((entry) => (
            <div key={entry.name} className={styles.legendItem}>
              <div
                className={styles.legendDot}
                style={{ background: VERTICAL_COLORS[entry.name] || FALLBACK_COLOR }}
              />
              <div className={styles.legendText}>
                <span className={styles.legendName}>{entry.name}</span>
                <span className={styles.legendValue}>{entry.value} deals</span>
              </div>
            </div>
          ))}
        </div>
        {/* A2: deals whose themes map to no vertical used to vanish from this card without a word,
            leaving a total quietly below the pipeline count. */}
        {unclassified > 0 && (
          <div className={styles.footnote}>
            {unclassified} deal{unclassified === 1 ? '' : 's'} without a vertical
          </div>
        )}
      </div>
    </div>
  );
}
